/**
 * « Ce que ce virement dit » — côté sortant.
 *
 * ## Le cas qui a fait écrire ce panneau
 *
 * 165,00 € virés le 02/09/2026, dont 70,00 € restaient à placer, et une
 * communication coupée à 53 caractères en plein milieu d'un numéro :
 *
 *     CDV-NDSC-2601711 REMBOURSEMENT NC-26-00479 et NC-26-0
 *
 * L'écran demandait de chercher une note à la main, et ne disait ni ce que la
 * communication citait, ni ce que ces pièces valaient. Or tout était là : la
 * facture `CDV-NDSC-2601711` porte **deux** notes — `NC-26-00479`, déjà
 * remboursée par ce virement même, et `NC-26-00478`, 92,00 € encore à rendre.
 * Celle que la troncature a emportée se retrouve **par la facture**.
 *
 * ## Les trois règles, les mêmes que côté entrant
 *
 * 1. Les références sont lues avec les fonctions du rapprochement — un panneau
 *    qui lirait autrement expliquerait un écran qui n'existe pas.
 * 2. Une note déjà remboursée reste AFFICHÉE, marquée comme telle : c'est la
 *    réponse à « pourquoi ce virement ne trouve-t-il rien ? ».
 * 3. Rien ne s'applique tout seul. Le bouton écrit ce qu'il annonce.
 *
 * ## Ce qu'il ne regarde pas
 *
 * La dette de la famille. Demandé le 19 septembre 2026 : « Même si
 * l'utilisateur a une dette, il faut que l'admin puisse quand même rapprocher
 * le remboursement vu qu'il a bel et bien été fait en banque. » Le virement est
 * parti ; le rapprochement ne fait que nommer la pièce qui lui correspond.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, ChevronDown, ChevronRight, FileText, Loader2, Receipt, Undo2, Wallet,
} from 'lucide-react';
import clsx from 'clsx';
import {
  lireLaSortie, partsDeLaSortie, referencesDuVirement, type NoteResolue,
} from '../../../lib/payments/sortie';
import {
  lireLesEncaissementsCites, lireLesProvisionsCitees, lireLesProvisionsDesComptes,
  lireNotesCitees, placerRemboursement, type DepenseAPlacer,
} from '../../../lib/payments/refundActions.supabase';
import {
  provisionsARendre, type ProvisionARendre, type ProvisionDeFamille,
} from '../../../lib/payments/provisionARendre';
import {
  annulationsPossibles, type AnnulationDEncaissement, type EncaissementDeFacture,
} from '../../../lib/payments/annulerLEncaissement';
import { lireLesFamillesCitees } from '../../../lib/payments/famillesCitees';
import type { LectureDuCompte } from '../../../lib/payments/compteDuBeneficiaire';
import { messageErreur } from '../../../lib/payments/erreur';
import { LIBELLES_DE_DESTINEE, type Destinee } from '../../../lib/creditNotes/destinee';
import CompteDeLaFamille from './CompteDeLaFamille';
import LienFacture from './LienFacture';
import { euros } from './money';

export default function NotesCitees({
  depense, compte, agir,
}: {
  depense: DepenseAPlacer;
  /**
   * Le compte du bénéficiaire, tel que la page l'a lu pour toute la file.
   *
   * Il sert de SECONDE source de famille. Signalé le 20 septembre 2026 sur un
   * virement de 60,00 € : « il reconnaît le compte mais automatiquement il met
   * un numéro de facture dans la recherche à la main, ce qui casse le lien
   * avec le compte ». La communication citait `CDV-2506002-00072`, un numéro
   * qu'aucune facture ne porte ; le compte, lui, était formellement reconnu.
   */
  compte?: LectureDuCompte;
  agir: (quoi: string, action: () => Promise<void>) => Promise<void>;
}) {
  /* Le bouton se verrouille pendant son écriture : un double clic écrirait
     deux fois la même répartition, et la base ne refuserait la seconde que si
     elle dépassait — donc pas toujours. */
  const [enCours, setEnCours] = useState(false);
  const refs = useMemo(
    () => referencesDuVirement(depense.communicationRaw, depense.freeText),
    [depense.communicationRaw, depense.freeText],
  );

  const [notes, setNotes] = useState<NoteResolue[] | null>(null);
  const [provisions, setProvisions] = useState<ProvisionDeFamille[]>([]);
  const [encaissements, setEncaissements] = useState<EncaissementDeFacture[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [compteOuvert, setCompteOuvert] = useState<string | null>(null);

  /* Seul l'état `reconnu` porte une famille : `partage` en désigne plusieurs et
     `inconnu` aucune. Se servir des deux autres ferait proposer une provision
     au hasard, ce qui est pire que de ne rien proposer. */
  const familleDuCompte = compte?.etat === 'reconnu' ? compte.compte : null;

  const charger = useCallback(async () => {
    if (refs.notes.length === 0 && refs.factures.length === 0) {
      setNotes([]);
      /* Rien de cité, mais le compte peut suffire : c'est le cas du virement
         dont la communication porte un numéro mal frappé. */
      if (familleDuCompte) {
        try {
          setProvisions(await lireLesProvisionsDesComptes([familleDuCompte.userId]));
        } catch (e) {
          setErreur(messageErreur(e, 'Lecture de la provision impossible'));
        }
      }
      return;
    }
    try {
      /*
        Les deux ensemble. Un double paiement ne produit AUCUNE note de crédit :
        sans la seconde lecture, l'écran dirait « aucune note remboursable » et
        ne proposerait que d'écarter — c'est-à-dire de traiter de l'argent de
        famille comme une dépense de fonctionnement.
      */
      const [lues, dotations, recus] = await Promise.all([
        lireNotesCitees(refs, depense.id),
        lireLesProvisionsCitees(refs),
        /* Et ce que les factures citées ont ENCAISSÉ : un remboursement sans
           note de crédit n'annule pas une créance, il annule un encaissement. */
        lireLesEncaissementsCites(refs),
      ]);
      setNotes(lues);
      setEncaissements(recus);
      /*
        Les pièces citées d'abord ; le compte en RENFORT quand elles n'ont
        désigné personne. Jamais l'inverse : une facture citée nomme la famille
        de façon bien plus sûre qu'un compte bancaire, dont on sait seulement
        qu'il a déjà payé pour elle.
      */
      setProvisions(
        dotations.length > 0 || !familleDuCompte
          ? dotations
          : await lireLesProvisionsDesComptes([familleDuCompte.userId]),
      );
    } catch (e) {
      setErreur(messageErreur(e, 'Lecture des notes citées impossible'));
    }
    /* `unallocatedCents` est dans les dépendances à dessein : il change dès
       qu'une écriture a eu lieu, et c'est le seul signal qui dise que les
       soldes affichés ici viennent de bouger. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refs, depense.id, depense.unallocatedCents, familleDuCompte?.userId]);

  useEffect(() => { void charger(); }, [charger]);

  /* Rien de cité ET aucun compte reconnu : ce panneau n'aurait rien à dire, et
     un cadre vide se lit comme une panne. La recherche manuelle reste en
     dessous. Avec un compte reconnu, en revanche, il a tout à dire — c'est
     même le seul endroit où la famille apparaît. */
  if (refs.notes.length === 0 && refs.factures.length === 0 && !familleDuCompte) return null;

  if (erreur) {
    return (
      <p className="flex items-start gap-1.5 rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-800">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{erreur}</span>
      </p>
    );
  }

  if (notes === null) {
    return (
      <p className="flex items-center gap-2 text-sm text-gray-600">
        <Loader2 className="h-4 w-4 animate-spin" /> Lecture des notes citées…
      </p>
    );
  }

  const lecture = lireLaSortie(depense.unallocatedCents, notes);
  const parts = partsDeLaSortie(depense.unallocatedCents, notes);
  const aPlacer = parts.reduce((s, p) => s + p.montantCents, 0);
  const familles = [...new Map(
    notes.filter((n) => n.userId).map((n) => [n.userId as string, n.famille ?? 'Compte sans nom']),
  )];
  /* La famille n'est écrite sur le virement que si elle est SANS AMBIGUÏTÉ. */
  const familleUnique = familles.length === 1 ? familles[0][0] : null;

  /*
    Ce que les notes citées ne prennent pas. C'est ce montant-là qu'une
    provision peut rendre — pas le virement entier, sinon un virement qui solde
    déjà une note se verrait proposer de la payer deux fois.
  */
  const resteApresLesNotes = depense.unallocatedCents - aPlacer;
  const aRendre = resteApresLesNotes > 0
    ? provisionsARendre(resteApresLesNotes, provisions)
    : [];

  /*
    Les factures citées qui ont réellement encaissé. Celles qui n'ont jamais
    rien reçu ne sont pas montrées ici : il n'y a chez elles aucun encaissement
    à annuler, et un cadre qui l'annoncerait ne ferait qu'ajouter du bruit à un
    écran qui en a déjà.
  */
  const aAnnuler = resteApresLesNotes > 0
    ? annulationsPossibles(
      resteApresLesNotes,
      encaissements,
      /* La provision de chaque famille entre dans la lecture : elle distingue
         le double paiement — qui se rend par la provision — du remboursement
         qui annule un encaissement. Voir `ContexteDeLaFamille`. */
      new Map(provisions.map((p) => [p.userId, p.avoirCents])),
    ).filter((a) => a.facture.encaisseCents > 0)
    : [];

  /*
    Lequel des deux chemins en premier. Quand AUCUNE famille citée n'a de
    provision, en rendre une est impossible — c'est exactement ce qui est
    arrivé aux 49,00 € de `CDV-WBK-2601307` le 20 septembre 2026 : la seule
    porte ouverte était la provision, et les 49,00 € se sont évanouis dans le
    plancher d'un solde qui n'avait jamais rien reçu. L'annulation passe donc
    devant quand la provision n'a rien à dire.
  */
  const provisionMuette = aRendre.length === 0 || aRendre.every((p) => p.accord === 'aucune');
  const annulationDAbord = provisionMuette && aAnnuler.some((a) => a.annulableCents > 0);

  /*
    Les deux lectures désignent-elles la même famille ? Un virement du 25 mars
    2026 cite `NC-25-00589` et `CDV-MC-2600396` : la note est celle d'Andrei
    Arnaut, la facture celle de Beatriz Camargo Magalhaes. Un clic placerait
    69,00 € sur la note d'une famille qui n'a rien demandé. Voir
    `famillesCitees.ts`.
  */
  const desaccord = lireLesFamillesCitees(
    notes
      .filter((n) => n.creditNoteId && !n.parLaFacture)
      .map((n) => ({ userId: n.userId, famille: n.famille, piece: n.numero })),
    encaissements.map((e) => ({ userId: e.userId, famille: e.famille, piece: e.invoiceNumber })),
  );

  return (
    <div className="rounded-md border border-gray-200 bg-white p-3">
      <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
        <Receipt className="h-3.5 w-3.5" /> Ce que ce virement dit
      </p>

      <p className="mt-2 text-sm text-gray-800">{lecture.phrases.join(' ')}</p>

      {desaccord.phrase && (
        <p className="mt-2 flex items-start gap-1.5 rounded-md border border-red-300 bg-red-50 p-2 text-sm text-red-900">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{desaccord.phrase}</span>
        </p>
      )}

      {notes.length > 0 && (
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="py-1 text-left font-medium">Note citée</th>
              <th className="py-1 text-left font-medium">Famille</th>
              <th className="py-1 text-left font-medium">Facture</th>
              <th className="py-1 text-right font-medium">Note</th>
              <th className="py-1 text-right font-medium">Déjà remboursé</th>
              <th className="py-1 text-right font-medium">Remboursable</th>
              <th className="py-1 text-right font-medium">Ce virement</th>
            </tr>
          </thead>
          <tbody>
            {notes.map((n) => (
              <tr key={n.numero} className="border-t border-gray-100 align-top">
                <td className="py-1.5">
                  <span className="font-mono text-xs text-gray-900">{n.numero}</span>
                  {/*
                    D'où elle vient. « Par la facture » est la découverte utile :
                    c'est la note que la troncature de la communication avait
                    emportée, et que rien ne nommait.
                  */}
                  {n.parLaFacture && (
                    <span className="ml-1 rounded bg-indigo-100 px-1 py-0.5 text-[10px] font-medium text-indigo-900">
                      trouvée par la facture
                    </span>
                  )}
                  {!n.parLaFacture && n.origine === 'releve' && (
                    <span className="ml-1 rounded bg-amber-100 px-1 py-0.5 text-[10px] font-medium text-amber-900">
                      libellé du relevé
                    </span>
                  )}
                  {n.destinee && (
                    <span
                      className="mt-0.5 block text-[11px] text-gray-500"
                      title={LIBELLES_DE_DESTINEE[n.destinee as Destinee]?.explication}
                    >
                      {LIBELLES_DE_DESTINEE[n.destinee as Destinee]?.label ?? n.destinee}
                    </span>
                  )}
                </td>
                <td className="py-1.5 text-xs text-gray-700">
                  {n.famille ?? <span className="text-gray-400">note inconnue ici</span>}
                </td>
                <td className="py-1.5">
                  {n.invoiceNumber
                    ? <LienFacture invoiceNumber={n.invoiceNumber} />
                    : <span className="text-xs text-gray-400">—</span>}
                </td>
                <td className="py-1.5 text-right tabular-nums text-gray-700">
                  {n.creditNoteId ? euros(n.montantCents) : '—'}
                </td>
                <td className="py-1.5 text-right tabular-nums text-gray-700">
                  {n.creditNoteId ? euros(n.dejaRembourseCents) : '—'}
                </td>
                <td className={clsx(
                  'py-1.5 text-right tabular-nums',
                  n.remboursableCents > 0 ? 'font-medium text-amber-800' : 'text-gray-500',
                )}>
                  {n.creditNoteId ? euros(n.remboursableCents) : '—'}
                </td>
                <td className="py-1.5 text-right tabular-nums text-blue-900">
                  {n.deCeVirementCents > 0 ? euros(n.deCeVirementCents) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {(() => {
        const provision = aRendre.length > 0 ? (
            <RendreLaProvision
            key="provision"
            propositions={aRendre}
            depenseId={depense.id}
            parLeCompte={familleDuCompte ? compte?.ibanLisible ?? null : null}
            enCours={enCours}
            setEnCours={setEnCours}
            agir={agir}
          />
        ) : null;
        const annulation = aAnnuler.length > 0 ? (
          <AnnulerLEncaissement
            key="annulation"
            propositions={aAnnuler}
            depenseId={depense.id}
            enCours={enCours}
            setEnCours={setEnCours}
            agir={agir}
          />
        ) : null;
        return annulationDAbord ? [annulation, provision] : [provision, annulation];
      })()}

      {parts.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3">
          <p className="text-xs text-gray-600">
            {parts.map((p) => `${p.note.numero} ${euros(p.montantCents)}`).join(' · ')}
          </p>
          <button
            disabled={enCours}
            onClick={async () => {
              setEnCours(true);
              try {
                await agir(
                  `${euros(aPlacer)} placés sur ${parts.length === 1 ? 'la note citée' : `${parts.length} notes citées`}`,
                  () => placerRemboursement(
                    depense.id,
                    parts.map((p) => ({
                      target: 'credit_note' as const,
                      amountCents: p.montantCents,
                      creditNoteId: p.note.creditNoteId,
                      creditNoteNumber: p.note.numero,
                    })),
                    {
                      userId: familleUnique,
                      notes: 'Note citée par la communication du virement',
                    },
                  ),
                );
              } finally {
                setEnCours(false);
              }
            }}
            className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            <Undo2 className="h-4 w-4" />
            Placer {euros(aPlacer)} sur {parts.length === 1 ? 'cette note' : `ces ${parts.length} notes`}
          </button>
        </div>
      )}

      {familles.length > 0 && (
        <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Comptes concernés
          </p>
          {familles.map(([userId, nom]) => (
            <div key={userId}>
              <button
                onClick={() => setCompteOuvert(compteOuvert === userId ? null : userId)}
                className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                {compteOuvert === userId
                  ? <ChevronDown className="h-3.5 w-3.5" />
                  : <ChevronRight className="h-3.5 w-3.5" />}
                {nom}
              </button>
              {compteOuvert === userId && (
                <div className="mt-2">
                  {/*
                    Le compte complet : factures, notes de crédit nommées et
                    cliquables, versements reçus, avoir. Demandé le 19 septembre :
                    « pour le matchmaking, on doit pouvoir vérifier la situation
                    réelle du compte client ».
                  */}
                  <CompteDeLaFamille key={depense.unallocatedCents} userId={userId} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Rendre une provision, quand aucune note de crédit ne correspond.
 *
 * ## Pourquoi ce bloc existe
 *
 * Signalé le 20 septembre 2026 : « la maman a payé deux fois, du coup pas de
 * note de crédit à lier mais juste le remboursement de la provision ». L'écran
 * affichait « Aucune note remboursable ne correspond » et n'offrait que
 * « Classer hors périmètre familles » — ce qui aurait rangé de l'argent de
 * famille avec l'ONSS et les fournisseurs.
 *
 * ## Ce qu'il montre avant d'écrire
 *
 * La provision, son montant, et **d'où elle vient**. Une provision affichée
 * sans son origine se croit sur parole ; avec la note de l'imputation qui l'a
 * constituée — « Reliquat versé en provision » — l'administration reconnaît
 * son propre geste.
 *
 * ## Il n'empêche jamais
 *
 * Une famille sans provision, ou avec une provision plus petite que le
 * virement, reçoit un avertissement et **pas un blocage**. La règle posée le
 * 19 septembre vaut ici aussi : le virement est parti en banque, et le
 * rapprochement ne fait que nommer la pièce qui lui correspond.
 */
function RendreLaProvision({
  propositions, depenseId, parLeCompte, enCours, setEnCours, agir,
}: {
  propositions: ProvisionARendre[];
  depenseId: string;
  /** Le compte bancaire qui a désigné la famille, quand rien d'autre ne l'a. */
  parLeCompte: string | null;
  enCours: boolean;
  setEnCours: (v: boolean) => void;
  agir: (quoi: string, action: () => Promise<void>) => Promise<void>;
}) {
  return (
    <div className="mt-3 space-y-3 border-t border-gray-100 pt-3">
      <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
        <Wallet className="h-3.5 w-3.5" /> Rendre une provision
      </p>

      {propositions.map((p) => (
        <div
          key={p.famille.userId}
          className={clsx(
            'rounded-md border p-3',
            p.aVerifier ? 'border-amber-300 bg-amber-50' : 'border-emerald-200 bg-emerald-50',
          )}
        >
          <p className={clsx('text-sm', p.aVerifier ? 'text-amber-900' : 'text-emerald-900')}>
            {p.aVerifier && <AlertCircle className="mr-1 inline h-4 w-4 align-text-bottom" />}
            {p.phrase}
          </p>
          {p.famille.parLaFacture && (
            <p className="mt-1 text-xs text-gray-600">
              Famille trouvée par la facture citée{' '}
              <LienFacture invoiceNumber={p.famille.parLaFacture} />
            </p>
          )}
          {/*
            D'où vient la famille, quand aucune pièce citée ne l'a nommée. Une
            provision proposée sans son origine se croit sur parole ; ici
            l'origine est un compte bancaire, et cela se dit.
          */}
          {!p.famille.parLaFacture && parLeCompte && (
            <p className="mt-1 text-xs text-gray-600">
              Famille trouvée par le compte du bénéficiaire{' '}
              <span className="font-mono">{parLeCompte}</span> — aucune pièce citée ne la nomme.
            </p>
          )}
          <button
            disabled={enCours}
            onClick={async () => {
              setEnCours(true);
              try {
                await agir(
                  `${euros(p.proposeCents)} rendus sur la provision de ${p.famille.famille}`,
                  () => placerRemboursement(
                    depenseId,
                    [{
                      target: 'provision' as const,
                      amountCents: p.proposeCents,
                      provisionUserId: p.famille.userId,
                    }],
                    {
                      userId: p.famille.userId,
                      /*
                        Le motif est écrit sur l'imputation : six mois plus
                        tard, « remboursement de provision » explique pourquoi
                        aucune note de crédit n'y est attachée. Sans lui, la
                        ligne se lirait comme un oubli.
                      */
                      notes: p.famille.parLaFacture || !parLeCompte
                        ? 'Remboursement de provision — aucune note de crédit, double paiement'
                        : `Remboursement de provision — famille reconnue par le compte ${parLeCompte},`
                          + ' aucune pièce citée',
                    },
                  ),
                );
              } finally {
                setEnCours(false);
              }
            }}
            className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            <Undo2 className="h-4 w-4" />
            Rendre {euros(p.proposeCents)} à {p.famille.famille}
          </button>
        </div>
      ))}
    </div>
  );
}

/**
 * Annuler l'encaissement d'une facture : un mouvement qui en annule un autre.
 *
 * ## Pourquoi ce bloc existe
 *
 * Signalé le 20 septembre 2026, facture `CDV-WBK-2601307` : « La maman a
 * demandé à se faire rembourser les 49 € car un service tiers allait payer
 * cette facture. Le service n'a toujours pas payé mais nous l'avons
 * remboursée. La facture apparaît toujours en payée. Je devrais avoir le choix
 * de la facture que cette sortie modifie — pas de note de crédit dans ce cas,
 * mais un mouvement qui en annule un autre. »
 *
 * ## Ce que le bouton écrit, en partie double
 *
 * Une imputation `target: 'invoice'`. Le grand livre porte alors un
 * remboursement (+) en face de l'encaissement (−) qu'il reprend : le solde de
 * la famille redevient celui d'avant le paiement, et **la facture est de
 * nouveau due**. Aucune note de crédit n'est créée, parce qu'on ne renonce pas
 * à la créance — elle a seulement changé de débiteur attendu.
 *
 * ## Ce que l'écran offrait à la place
 *
 * « Rendre une provision », seule porte ouverte. Elle a été prise, et les
 * 49,00 € ont été rendus sur une provision qui n'avait jamais rien reçu :
 * `avoir_cents` vaut `GREATEST(0, versé − rendu)`, le montant s'est donc
 * évanoui dans le plancher pendant que la facture restait `paid`. Trois autres
 * virements de la production citent une facture sans note de crédit — ceux-là
 * rendent une vraie provision, et le chemin d'à côté reste le bon pour eux.
 */
function AnnulerLEncaissement({
  propositions, depenseId, enCours, setEnCours, agir,
}: {
  propositions: AnnulationDEncaissement[];
  depenseId: string;
  enCours: boolean;
  setEnCours: (v: boolean) => void;
  agir: (quoi: string, action: () => Promise<void>) => Promise<void>;
}) {
  return (
    <div className="mt-3 space-y-3 border-t border-gray-100 pt-3">
      <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
        <FileText className="h-3.5 w-3.5" /> Annuler l&apos;encaissement d&apos;une facture
      </p>

      {propositions.map((a) => (
        <div
          key={a.facture.invoiceId}
          className={clsx(
            'rounded-md border p-3',
            a.aVerifier ? 'border-amber-300 bg-amber-50' : 'border-emerald-200 bg-emerald-50',
          )}
        >
          <p className={clsx('text-sm', a.aVerifier ? 'text-amber-900' : 'text-emerald-900')}>
            {a.aVerifier && <AlertCircle className="mr-1 inline h-4 w-4 align-text-bottom" />}
            {a.phrase}
          </p>
          <p className="mt-1 text-xs text-gray-600">
            <LienFacture invoiceNumber={a.facture.invoiceNumber} />
            {a.facture.famille && <> · {a.facture.famille}</>}
            {/*
              Aucune note de crédit n'est créée : c'est la différence entre ce
              geste et tous les autres remboursements de cet écran, et elle
              mérite d'être écrite là où l'on clique.
            */}
            {' '}· aucune note de crédit ne sera créée
          </p>
          <button
            disabled={enCours}
            onClick={async () => {
              setEnCours(true);
              try {
                await agir(
                  `${euros(a.proposeCents)} repris sur l'encaissement de ${a.facture.invoiceNumber}`,
                  () => placerRemboursement(
                    depenseId,
                    [{
                      target: 'invoice' as const,
                      amountCents: a.proposeCents,
                      invoiceId: a.facture.invoiceId,
                    }],
                    {
                      userId: a.facture.userId,
                      /*
                        Le motif est écrit sur l'imputation : six mois plus
                        tard, il explique pourquoi cette facture est repassée
                        de « payée » à « due » sans qu'aucune note ne
                        l'accompagne.
                      */
                      notes: `Annule l'encaissement de ${a.facture.invoiceNumber} `
                        + '— remboursement sans note de crédit, la facture redevient due',
                    },
                  ),
                );
              } finally {
                setEnCours(false);
              }
            }}
            className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            <Undo2 className="h-4 w-4" />
            Annuler {euros(a.proposeCents)} encaissés sur {a.facture.invoiceNumber}
          </button>
        </div>
      ))}
    </div>
  );
}
